import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import {
  ReportRequestParams,
  SubmissionsResponse,
  extraFiltersOnly,
} from 'src/app/common/dtos/reports.dto';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { PaginationDTO } from 'src/app/common/types/responses/responses-template';
import { PdSession } from '../../../dtos/pd.dto';
import { ProformViewStates } from '../../../dtos/proforms.dto';
import { ProformsSubmissionService } from '../../../services/proforms/proforms-submission.service';
import { ProFormType } from '../../../types/responses/proforms.responses';
import { ModalComponent } from '../../modals/modal/modal.component';
import { checkDefaultChartFilters } from '../report-controller/chart-options-helper';

@Component({
  selector: 'app-proforms-submissions',
  standalone: false,
  templateUrl: './proforms-submissions.component.html',
  styleUrl: './proforms-submissions.component.scss',
})
export class ProformsSubmissionsComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() formId: number;

  @Input() requestParams: ReportRequestParams;

  @Input() selectedType: ProFormType;

  @Output() readonly numSubmissionsChange: EventEmitter<number> =
    new EventEmitter();

  loading = true;

  user: User;

  params: ReportRequestParams[] = [];

  private destroy$ = new Subject<void>();

  private loadRequest$ = new Subject<ReportRequestParams>();

  formSubmissions: SubmissionsResponse[];

  listMeta?: PaginationDTO;

  // QUIZ PREVIEW
  viewStates = ProformViewStates;

  selectedSubmissionId?: number;

  session: PdSession;

  @ViewChild('submissionModal')
  submissionModal: ModalComponent;

  constructor(
    private apiService: APICoreService,
    private store: Store,
    private proformsSubmissionService: ProformsSubmissionService
  ) {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
  }

  ngOnInit(): void {
    this.loadRequest$
      .pipe(
        switchMap((params) => {
          this.loading = true;
          return this.apiService.getRequest('reporting/load-report', params);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((value) => {
        this.formSubmissions = value.items as SubmissionsResponse[];
        this.listMeta = {
          currentPage: value.currentPage,
          pageCount: value.pageCount,
          perPage: value.perPage,
          totalCount: value.totalCount,
        };
        this.numSubmissionsChange.emit(value.totalCount);
        this.loading = false;
      });
    this.initializeController();
  }

  ngOnChanges(change: SimpleChanges) {
    const requestParamsChanged =
      change['requestParams'] && !change['requestParams'].firstChange;
    const formIdChanged = change['formId'] && !change['formId'].firstChange;
    if (requestParamsChanged || formIdChanged) {
      this.initializeController();
    }
  }

  initializeController() {
    this.params = [JSON.parse(JSON.stringify(this.requestParams))];
    this.getSubmissionData();
  }

  openSubmissionModal(sessionId: number) {
    this.selectedSubmissionId = sessionId;
    this.proformsSubmissionService.getSubmission(sessionId);
    setTimeout(() => {
      this.submissionModal.open();
    }, 600);
  }

  resetModal() {
    this.selectedSubmissionId = undefined;
    this.proformsSubmissionService.resetNewSubmission();
  }

  getSubmissionData(): void {
    const params = checkDefaultChartFilters(
      this.params[this.params.length - 1],
      this.user
    );
    this.loadRequest$.next({
      ...params,
      filters:
        extraFiltersOnly(params.filters, params.report_filterset_id) ?? [],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  changePage(currentPage: number) {
    this.params = [
      {
        ...this.params[this.params.length - 1],
        page: currentPage,
      },
    ];
    this.getSubmissionData();
  }
}
